<template>
  <a-row :gutter="24">
    <a-col :md="24">
      <a-card :bordered="false">
        <!-- ========== 顶部：单据信息与操作 ========== -->
        <div class="workbench-header">
          <div class="workbench-title">
            <a-button icon="arrow-left" @click="goBack" style="margin-right: 12px">返回</a-button>
            <span>发图单：{{ order.billNo || '-' }}</span>
            <a-tag v-if="order.status === '0'" color="orange" style="margin-left: 8px">待校正</a-tag>
            <a-tag v-else color="green" style="margin-left: 8px">已生成订单 {{ order.orderHeadId ? '(' + order.orderHeadId + ')' : '' }}</a-tag>
            <span style="margin-left: 16px; color: #888">客户：{{ order.customerName || '-' }}</span>
          </div>
          <div v-if="order.status === '0'">
            <a-button v-if="!xsddMultiOn" type="primary" icon="check"
                      :disabled="!canGenerate" :loading="generating"
                      @click="handleGenerate(false)">生成订单</a-button>
            <a-button v-if="!xsddMultiOn || !xsddFlowOn" type="primary" icon="check"
                      style="margin-left: 8px" :disabled="!canGenerate" :loading="generating"
                      @click="handleGenerate(true)">生成并审核</a-button>
            <a-button v-if="xsddMultiOn && xsddFlowOn" type="primary" icon="check"
                      :disabled="!canGenerate" :loading="generating"
                      @click="handleGenerateFlow">生成并提交流程</a-button>
          </div>
        </div>
        <a-alert v-if="order.status === '0' && images.length > 0 && !allCorrected"
                 type="info" show-icon style="margin-bottom: 12px"
                 message="逐张图片完成 OCR 识别 → 点击行框挂接商品/数量 → 保存本图校正；全部图片校正完成后可生成销售订单" />
        <a-alert v-if="!orderId" type="warning" show-icon
                 message="缺少发图单参数，请从【收单中心 → 发图订单】列表进入" />

        <a-row :gutter="16" v-if="orderId && images.length > 0">
          <!-- ========== 左侧：图片列表 ========== -->
          <a-col :span="5">
            <div class="img-list">
              <div v-for="img in images" :key="img.id"
                   class="img-item" :class="{ active: currentImageId === img.id }"
                   @click="selectImage(img.id)">
                <img :src="img.imgUrl" class="img-thumb" />
                <div class="img-item-footer">
                  <span class="img-item-name">图{{ img.id }}</span>
                  <a-tag class="img-item-tag" :color="ocrStatusColor(img.ocrStatus)">{{ ocrStatusName(img.ocrStatus) }}</a-tag>
                </div>
              </div>
            </div>
          </a-col>

          <!-- ========== 中间：选中图 + OCR 框选层 ========== -->
          <a-col :span="19">
            <div class="img-toolbar">
              <a-button v-if="order.status === '0'" type="primary" icon="scan"
                        :disabled="!currentImage || currentImage.ocrStatus !== '0'" :loading="ocrRunning"
                        @click="handleRunOcr">识别图片(OCR)</a-button>
              <a-button v-if="order.status === '0'" style="margin-left: 8px"
                        :type="drawMode ? 'primary' : 'dashed'" icon="edit"
                        :disabled="!currentImage || currentImage.ocrStatus === '0'"
                        @click="toggleDrawMode">增加标注</a-button>
              <a-button v-if="order.status === '0'" style="margin-left: 8px"
                        :disabled="!currentImage || currentImage.ocrStatus === '0'" :loading="saving"
                        @click="handleSaveCorrections">保存本图校正</a-button>
              <span v-if="currentImage" class="img-toolbar-hint">
                单击框选中（可拖动/缩放/Delete删除），双击框编辑内容，增加标注补漏识别行
              </span>
            </div>

            <div v-if="currentImage" class="img-stage-wrap">
              <!-- 图片框选层：按 OCR 位置比例渲染；单击选中、双击编辑、选中后拖动/缩放/Delete 删除 -->
              <div v-if="lines.length > 0 && naturalWidth > 0"
                   class="img-stage" :class="{ 'draw-mode': drawMode }" @click="onStageClick">
                <img :src="currentImage.imgUrl" class="img-main" ref="mainImage"
                     @load="onImgLoad" @error="onImgError" @mousedown="onDrawMouseDown" />
                <div v-for="line in positionedLines" :key="line.lineIndex"
                     class="ocr-box"
                     :class="[lineColor(line) === 'green' ? 'ocr-box-green' : 'ocr-box-red',
                              { 'ocr-box-selected': line.lineIndex === selectedLineIndex }]"
                     :style="boxStyle(line)" :title="line.text"
                     @click.stop="selectLine(line)"
                     @dblclick.stop="openLine(line)"
                     @mousedown.stop="onBoxMouseDown($event, line)">
                  <span class="ocr-box-index">{{ line.lineIndex >= 0 ? line.lineIndex + 1 : '新' }}</span>
                  <span v-if="edits[line.lineIndex] && edits[line.lineIndex].saveAlias"
                        class="ocr-box-alias" title="已勾选记为别名">别</span>
                  <span v-if="line.lineIndex === selectedLineIndex" class="ocr-box-resize"
                        @mousedown.stop="onResizeMouseDown($event, line)"></span>
                </div>
                <div v-if="drawingBox" class="drawing-box" :style="boxStyle(drawingBox)"></div>
              </div>
              <div v-else-if="!imgLoadError" class="img-stage">
                <img :src="currentImage.imgUrl" class="img-main" ref="mainImage" @load="onImgLoad" @error="onImgError" />
              </div>
              <a-alert v-if="imgLoadError" type="error" show-icon
                       :message="'图片加载失败：' + currentImage.imgUrl" />

              <!-- 无位置信息的行降级为列表 -->
              <div v-if="plainLines.length > 0" class="plain-lines">
                <div class="plain-lines-title">以下识别行无位置信息（点击文字编辑，尾部 × 删除）：</div>
                <div v-for="line in plainLines" :key="line.lineIndex" class="plain-line">
                  <a-tag :color="lineColor(line) === 'green' ? 'green' : 'red'">{{ line.lineIndex + 1 }}</a-tag>
                  <span class="plain-line-text" @click="openLine(line)">{{ line.text }}</span>
                  <a-icon v-if="order.status === '0'" type="close" class="plain-line-del"
                          title="删除该行" @click="deleteLineByIndex(line.lineIndex)" />
                </div>
              </div>
              <div v-if="lines.length > 0 && naturalWidth === 0 && !imgLoadError"
                   class="plain-lines-hint">图片加载中，行位置框将在图片加载后显示</div>
            </div>
            <a-empty v-else description="请从左侧选择图片" style="margin-top: 80px" />
          </a-col>
        </a-row>
        <a-empty v-else-if="orderId" description="该发图单暂无图片" style="margin-top: 80px" />
      </a-card>
    </a-col>

    <!-- ========== 行校正弹窗（标题栏可拖动，防遮挡标注文本） ========== -->
    <a-modal
      :title="editingLine.lineIndex >= 0
        ? '校正第 ' + (editingLine.lineIndex + 1) + ' 行' : '新增标注行'"
      :width="560"
      :visible="lineModalVisible"
      :confirmLoading="lineSaving"
      :maskClosable="false"
      wrapClassName="line-edit-modal"
      @ok="handleLineOk"
      @cancel="lineModalVisible = false"
      cancelText="取消"
      okText="确定"
      style="top:100px;">
      <a-form>
        <a-form-item :labelCol="modalLabelCol" :wrapperCol="modalWrapperCol" label="识别文字" required>
          <a-input placeholder="识别出的文字，可修改" v-model="editingLine.text" @blur="onTextBlur" />
        </a-form-item>
        <a-form-item :labelCol="modalLabelCol" :wrapperCol="modalWrapperCol" label="挂接商品"
                     extra="搜索选择本租户商品；不挂接商品或不填数量的行不会进入订单">
          <a-select
            placeholder="输入名称搜索商品"
            v-model="editingLine.materialId"
            show-search
            allow-clear
            :filter-option="false"
            @search="handleSearchMaterial">
            <a-select-option v-for="m in materialList" :key="m.id" :value="m.id">
              {{ m.unit ? m.name + '（' + m.unit + '）' : m.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :labelCol="modalLabelCol" :wrapperCol="modalWrapperCol" label="数量">
          <a-input-number style="width: 100%" :min="0" :precision="2" placeholder="请输入数量" v-model="editingLine.quantity" />
        </a-form-item>
        <a-form-item :labelCol="modalLabelCol" :wrapperCol="modalWrapperCol" label="单位"
                     :extra="unitHint">
          <a-input placeholder="留空则按商品档案单位" v-model="editingLine.unit" />
        </a-form-item>
        <a-form-item :labelCol="modalLabelCol" :wrapperCol="modalWrapperCol" label="备注">
          <a-input placeholder="选填，如多数量原文（3斤+8斤）/规格说明" v-model="editingLine.remark" />
        </a-form-item>
        <a-form-item :labelCol="modalLabelCol" :wrapperCol="modalWrapperCol" label="记为别名"
                     extra="勾选后将该行原始识别文字的商品名段记为所挂接商品的别名（错字→正确商品映射），下次OCR自动命中">
          <a-checkbox :disabled="!editingLine.materialId" v-model="editingLine.saveAlias" />
        </a-form-item>
      </a-form>
    </a-modal>
  </a-row>
</template>
<script>
import {
  getImgOrder, getImgOrderImage, runImgOcr, saveImgCorrections, generateImgOrder, getMaterials,
  getImgAuditConfig, submitWorkflowTask, parseImgLine
} from '@/api/plugin/orderReceive'

export default {
  name: 'OcrWorkbench',
  data() {
    return {
      orderId: null,
      order: {},
      images: [],
      currentImageId: null,
      imageDetail: null,
      // XSDD 生成按钮三态配置（进入页面时加载）
      xsddMultiOn: false,
      xsddFlowOn: false,
      // 行编辑状态：lineIndex -> {text, materialId, materialName, quantity, unit, remark, fileUnit, saveAlias}
      edits: {},
      naturalWidth: 0,
      naturalHeight: 0,
      imgLoadError: false,
      ocrRunning: false,
      saving: false,
      generating: false,
      lineModalVisible: false,
      lineSaving: false,
      materialList: [],
      // ===== 行编辑状态（需求3：删除/新增标注/选中移动缩放）=====
      deletedLineIndexes: [],
      localAddedLines: [],
      nextTempIndex: -1,
      selectedLineIndex: null,
      drawMode: false,
      drawingBox: null,
      editingLine: {
        lineIndex: -1,
        text: '',
        materialId: undefined,
        materialName: '',
        quantity: null,
        unit: '',
        remark: '',
        fileUnit: '',
        saveAlias: false
      },
      modalLabelCol: { span: 5 },
      modalWrapperCol: { span: 18 }
    }
  },
  computed: {
    // 当前选中图片对象（基础信息）
    currentImage() {
      return this.images.find((img) => img.id === this.currentImageId) || null
    },
    // 当前图片 OCR 行：过滤本次已删除行 + 合并本地新增标注行（保存后刷新转正）
    lines() {
      const base = (this.imageDetail && this.imageDetail.lines) || []
      return base
        .filter((l) => this.deletedLineIndexes.indexOf(l.lineIndex) < 0)
        .concat(this.localAddedLines)
    },
    // 有位置信息的行（渲染框选层）
    positionedLines() {
      return this.lines.filter((l) => l.left != null && this.naturalWidth > 0)
    },
    // 无位置信息的行（降级列表）
    plainLines() {
      return this.lines.filter((l) => l.left == null)
    },
    // 全部图片校正完成才可生成订单
    allCorrected() {
      return this.images.length > 0 && this.images.every((img) => img.ocrStatus === '2')
    },
    canGenerate() {
      return this.allCorrected && this.order.status === '0'
    },
    // 编辑行挂接商品的档案单位（下拉选中项优先，兜底 OCR 行带回的 fileUnit）
    editingFileUnit() {
      const selected = this.materialList.find((m) => m.id === this.editingLine.materialId)
      return (selected && selected.unit) || this.editingLine.fileUnit || ''
    },
    // 单位提示：档案单位与填写单位不一致时，说明按客户下单口径记录
    unitHint() {
      const fileUnit = this.editingFileUnit
      if (!fileUnit) {
        return ''
      }
      const unit = this.editingLine.unit || ''
      if (unit && unit !== fileUnit) {
        return '商品档案单位：' + fileUnit + '；按客户下单单位“' + unit + '”记录，称重换算在出库环节'
      }
      return '商品档案单位：' + fileUnit
    }
  },
  created() {
    this.orderId = this.$route.query.id ? Number(this.$route.query.id) : null
    if (this.orderId) {
      this.loadOrder()
    }
    // 提前并行加载 XSDD 审核配置，决定生成按钮三态
    this.loadXsddAuditConfig()
  },
  mounted() {
    window.addEventListener('keydown', this.onDeleteKeyDown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onDeleteKeyDown)
  },
  watch: {
    // 同路由不同记录（仅 query.id 变化）时组件被复用（含 keep-alive 缓存），
    // created 不会再执行，需监听路由变化重载详情
    '$route'(to) {
      if (to.path !== '/plugin/order-receive/imgorder/workbench') {
        return
      }
      const newId = to.query.id ? Number(to.query.id) : null
      if (newId && newId !== this.orderId) {
        this.orderId = newId
        this.resetPageState()
        this.loadOrder()
      }
    },
    // 弹窗打开后启用标题栏拖动；关闭时解绑并复位位置
    lineModalVisible(visible) {
      if (visible) {
        this.$nextTick(this.initModalDrag)
      } else {
        this.resetModalDrag()
      }
    }
  },
  methods: {
    // 加载发图单详情并默认选中第一张图
    loadOrder() {
      getImgOrder(this.orderId).then((res) => {
        if (res.code === 200) {
          this.order = res.data || {}
          this.images = (this.order.images) || []
          if (this.images.length > 0 && !this.images.some((img) => img.id === this.currentImageId)) {
            this.selectImage(this.images[0].id)
          }
        } else {
          this.$message.warning(res.message || '查询失败')
        }
      })
    },
    // 选中图片并加载 OCR 行/校正状态
    selectImage(imageId) {
      this.currentImageId = imageId
      this.naturalWidth = 0
      this.naturalHeight = 0
      this.imgLoadError = false
      this.imageDetail = null
      this.resetLineEditState()
      getImgOrderImage(this.orderId, imageId).then((res) => {
        if (res.code === 200) {
          this.imageDetail = res.data || null
          this.edits = this.buildEdits(this.imageDetail)
        } else {
          this.$message.warning(res.message || '查询失败')
        }
      })
    },
    // 行编辑本地状态复位（切图/重新识别/保存成功后调用，防止索引错位）
    resetLineEditState() {
      this.deletedLineIndexes = []
      this.localAddedLines = []
      this.nextTempIndex = -1
      this.selectedLineIndex = null
      this.drawMode = false
      this.drawingBox = null
    },
    // 切换到另一条发图单时重置整页状态（复用实例的旧单据数据全部清空）
    resetPageState() {
      this.order = {}
      this.images = []
      this.currentImageId = null
      this.imageDetail = null
      this.edits = {}
      this.naturalWidth = 0
      this.naturalHeight = 0
      this.imgLoadError = false
      this.resetLineEditState()
    },
    // 初始化/合并行编辑状态：OCR 行（含解析结果与别名自动映射）+ 已保存校正结果
    buildEdits(detail) {
      const map = {}
      ;(detail && detail.lines || []).forEach((l) => {
        map[l.lineIndex] = {
          text: l.text || '',
          materialId: l.materialId || undefined,
          materialName: l.materialName || '',
          quantity: l.quantity != null ? Number(l.quantity) : null,
          unit: l.unit || '',
          remark: l.remark || '',
          fileUnit: l.fileUnit || '',
          saveAlias: false
        }
      })
      ;(detail && detail.corrections || []).forEach((c) => {
        map[c.lineIndex] = {
          text: c.text || '',
          materialId: c.materialId || undefined,
          materialName: c.materialName || '',
          quantity: c.quantity != null ? Number(c.quantity) : null,
          unit: c.unit || '',
          remark: c.remark || '',
          fileUnit: (map[c.lineIndex] && map[c.lineIndex].fileUnit) || '',
          saveAlias: false
        }
      })
      return map
    },
    // 图片加载完成：记录原始尺寸用于按比例渲染框
    onImgLoad(e) {
      this.naturalWidth = e.target.naturalWidth || 0
      this.naturalHeight = e.target.naturalHeight || 0
    },
    onImgError() {
      this.imgLoadError = true
      this.naturalWidth = 0
      this.naturalHeight = 0
    },
    // 行框颜色：已校正或高置信度绿框，未校正低置信度红框
    lineColor(line) {
      const edit = this.edits[line.lineIndex]
      if (this.currentImage && this.currentImage.ocrStatus === '2') {
        return 'green'
      }
      if (edit && edit.materialId) {
        return 'green'
      }
      return line.status === '2' ? 'green' : 'red'
    },
    // 框选层位置：OCR 像素坐标 / 图片原始宽度 换算为百分比
    boxStyle(line) {
      return {
        left: (line.left / this.naturalWidth * 100) + '%',
        top: (line.top / this.naturalHeight * 100) + '%',
        width: (line.width / this.naturalWidth * 100) + '%',
        height: (line.height / this.naturalHeight * 100) + '%'
      }
    },
    ocrStatusName(status) {
      return { '0': '未识别', '1': '待校正', '2': '已校正' }[status] || status
    },
    ocrStatusColor(status) {
      return { '0': 'default', '1': 'orange', '2': 'green' }[status] || ''
    },
    // 触发单图 OCR 识别
    handleRunOcr() {
      this.ocrRunning = true
      runImgOcr(this.orderId, this.currentImageId).then((res) => {
        if (res.code === 200) {
          this.$message.success('识别成功')
          this.resetLineEditState()
          this.imageDetail = res.data || null
          this.edits = this.buildEdits(this.imageDetail)
          this.loadOrder()
        } else {
          this.$message.warning(res.message || '识别失败')
        }
      }).finally(() => {
        this.ocrRunning = false
      })
    },
    // 识别文字失焦：实时解析（标点清洗+四元组）+匹配（客户别名→通用别名→档案名），
    // 自动回填挂接商品/数量/单位/备注；解析为空的字段不覆盖已有手工值
    onTextBlur() {
      const text = this.editingLine.text == null ? '' : this.editingLine.text.trim()
      if (!text || !this.orderId) {
        return
      }
      parseImgLine(this.orderId, { text }).then((res) => {
        if (res.code !== 200 || !res.data) {
          return
        }
        const d = res.data
        if (d.quantity != null) {
          this.editingLine.quantity = Number(d.quantity)
        }
        if (d.unit) {
          this.editingLine.unit = d.unit
        }
        if (d.remark) {
          this.editingLine.remark = d.remark
        }
        if (d.fileUnit) {
          this.editingLine.fileUnit = d.fileUnit
        }
        if (d.materialId != null) {
          this.editingLine.materialId = d.materialId
          this.editingLine.materialName = d.materialName || ''
          // 命中商品不在当前下拉选项时注入，保证选中项正确显示
          if (!this.materialList.some((m) => m.id === d.materialId)) {
            this.materialList.unshift({ id: d.materialId, name: d.materialName || '', unit: d.fileUnit || '' })
          }
          this.$message.success('已匹配：' + (d.materialName || ''))
        } else {
          this.$message.info('未匹配到商品，请手动挂接')
        }
      })
    },

    // ===== 框选编辑交互（需求3/4）=====
    // 切换"增加标注"画框模式
    toggleDrawMode() {
      this.drawMode = !this.drawMode
      if (!this.drawMode) {
        this.drawingBox = null
      }
    },
    // 画框模式：图片上按下拖拽画新框，松开后弹校正弹窗（漏识别行手动补录）
    onDrawMouseDown(e) {
      if (!this.drawMode || this.order.status !== '0') {
        return
      }
      e.preventDefault()
      const img = this.$refs.mainImage
      if (!img) {
        return
      }
      const rect = img.getBoundingClientRect()
      const scaleX = this.naturalWidth / rect.width
      const scaleY = this.naturalHeight / rect.height
      const x0 = (e.clientX - rect.left) * scaleX
      const y0 = (e.clientY - rect.top) * scaleY
      const onMove = (ev) => {
        const x1 = Math.max(0, Math.min((ev.clientX - rect.left) * scaleX, this.naturalWidth))
        const y1 = Math.max(0, Math.min((ev.clientY - rect.top) * scaleY, this.naturalHeight))
        this.drawingBox = {
          left: Math.min(x0, x1),
          top: Math.min(y0, y1),
          width: Math.abs(x1 - x0),
          height: Math.abs(y1 - y0)
        }
      }
      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        const box = this.drawingBox
        this.drawingBox = null
        if (box && box.width >= 10 && box.height >= 10) {
          this.openNewLine(box)
        }
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },
    // 单击框选中（再次单击已选中框则进入拖动，见 onBoxMouseDown）
    selectLine(line) {
      if (this.order.status !== '0') {
        return
      }
      this.selectedLineIndex = line.lineIndex
    },
    // 空白处单击取消选中
    onStageClick() {
      this.selectedLineIndex = null
    },
    // 框上按下：已选中→启动拖动移动；未选中→仅选中（本次 click 完成选中）
    onBoxMouseDown(e, line) {
      if (this.order.status !== '0') {
        return
      }
      if (this.selectedLineIndex !== line.lineIndex) {
        return
      }
      this.startBoxDrag(e, line, 'move')
    },
    // 右下角缩放手柄（选中后显示）
    onResizeMouseDown(e, line) {
      if (this.order.status !== '0') {
        return
      }
      this.startBoxDrag(e, line, 'resize')
    },
    // 框拖动/缩放：屏幕位移换算原图像素，位置直接写行对象（随"保存本图校正"提交）
    startBoxDrag(e, line, mode) {
      e.preventDefault()
      const img = this.$refs.mainImage
      if (!img) {
        return
      }
      const scaleX = this.naturalWidth / img.clientWidth
      const scaleY = this.naturalHeight / img.clientHeight
      const startX = e.clientX
      const startY = e.clientY
      const orig = { left: line.left, top: line.top, width: line.width, height: line.height }
      const onMove = (ev) => {
        const dx = (ev.clientX - startX) * scaleX
        const dy = (ev.clientY - startY) * scaleY
        if (mode === 'move') {
          line.left = Math.round(Math.max(0, Math.min(orig.left + dx, this.naturalWidth - orig.width)))
          line.top = Math.round(Math.max(0, Math.min(orig.top + dy, this.naturalHeight - orig.height)))
        } else {
          line.width = Math.round(Math.max(8, Math.min(orig.width + dx, this.naturalWidth - orig.left)))
          line.height = Math.round(Math.max(8, Math.min(orig.height + dy, this.naturalHeight - orig.top)))
        }
      }
      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },
    // Delete 键删除选中行（弹窗打开/无选中/已生成订单时不响应）
    onDeleteKeyDown(e) {
      if (e.key !== 'Delete' || this.lineModalVisible || this.selectedLineIndex == null) {
        return
      }
      if (this.order.status !== '0') {
        return
      }
      this.deleteLineByIndex(this.selectedLineIndex)
    },
    // 删除行：已有行入删除集（保存时后端从 ocr_raw 物理删除并重排），本地新增行直接移除
    deleteLineByIndex(lineIndex) {
      if (lineIndex >= 0) {
        if (this.deletedLineIndexes.indexOf(lineIndex) < 0) {
          this.deletedLineIndexes.push(lineIndex)
        }
      } else {
        this.localAddedLines = this.localAddedLines.filter((l) => l.lineIndex !== lineIndex)
      }
      this.$delete(this.edits, lineIndex)
      if (this.selectedLineIndex === lineIndex) {
        this.selectedLineIndex = null
      }
    },
    // 新增标注行：画框完成后弹出校正弹窗（文字手动输入，失焦自动匹配）
    openNewLine(box) {
      this.editingLine = {
        lineIndex: -1,
        text: '',
        materialId: undefined,
        materialName: '',
        quantity: null,
        unit: '',
        remark: '',
        fileUnit: '',
        saveAlias: false,
        left: Math.round(box.left),
        top: Math.round(box.top),
        width: Math.round(box.width),
        height: Math.round(box.height)
      }
      this.loadMaterials('', true)
      this.lineModalVisible = true
    },
    // 校正弹窗标题栏拖动（防遮挡标注文本）：fixed 定位跟随鼠标，关闭复位
    initModalDrag() {
      const wrap = document.querySelector('.line-edit-modal')
      const modal = wrap && wrap.querySelector('.ant-modal')
      const header = wrap && wrap.querySelector('.ant-modal-header')
      if (!modal || !header) {
        return
      }
      // 防重复绑定：弹窗 DOM 复用时先解绑旧 handler
      if (this._dragHeaderEl && this._dragHandler) {
        this._dragHeaderEl.removeEventListener('mousedown', this._dragHandler)
      }
      this._dragModalEl = modal
      this._dragHeaderEl = header
      const onMouseDown = (e) => {
        const rect = modal.getBoundingClientRect()
        const offsetX = e.clientX - rect.left
        const offsetY = e.clientY - rect.top
        modal.style.position = 'fixed'
        modal.style.margin = '0'
        const onMove = (ev) => {
          modal.style.left = (ev.clientX - offsetX) + 'px'
          modal.style.top = (ev.clientY - offsetY) + 'px'
        }
        const onUp = () => {
          document.removeEventListener('mousemove', onMove)
          document.removeEventListener('mouseup', onUp)
        }
        document.addEventListener('mousemove', onMove)
        document.addEventListener('mouseup', onUp)
        e.preventDefault()
      }
      header.addEventListener('mousedown', onMouseDown)
      this._dragHandler = onMouseDown
    },
    resetModalDrag() {
      if (this._dragHeaderEl && this._dragHandler) {
        this._dragHeaderEl.removeEventListener('mousedown', this._dragHandler)
      }
      this._dragHeaderEl = null
      this._dragHandler = null
      if (this._dragModalEl) {
        this._dragModalEl.style.position = ''
        this._dragModalEl.style.margin = ''
        this._dragModalEl.style.left = ''
        this._dragModalEl.style.top = ''
        this._dragModalEl = null
      }
    },

    // 打开行校正弹窗
    openLine(line) {
      const edit = this.edits[line.lineIndex] || {}
      this.editingLine = {
        lineIndex: line.lineIndex,
        text: edit.text != null ? edit.text : (line.text || ''),
        materialId: edit.materialId,
        materialName: edit.materialName || '',
        quantity: edit.quantity != null ? edit.quantity : null,
        unit: edit.unit != null ? edit.unit : (line.unit || ''),
        remark: edit.remark != null ? edit.remark : (line.remark || ''),
        fileUnit: line.fileUnit || '',
        saveAlias: !!(edit && edit.saveAlias)
      }
      this.loadMaterials('', true)
      this.lineModalVisible = true
    },
    // 商品下拉（名称前缀搜索；keepCurrent 保证已挂接商品（如别名自动映射）始终有显示项）
    loadMaterials(name, keepCurrent) {
      getMaterials({ name: name || '' }).then((res) => {
        if (res.code === 200) {
          const list = res.data || []
          if (keepCurrent && this.editingLine.materialId &&
              !list.some((m) => m.id === this.editingLine.materialId)) {
            list.unshift({ id: this.editingLine.materialId, name: this.editingLine.materialName, unit: '' })
          }
          this.materialList = list
        }
      })
    },
    handleSearchMaterial(value) {
      this.loadMaterials(value, true)
    },
    // 弹窗确定：更新本地行编辑状态
    handleLineOk() {
      if (!this.editingLine.text || !this.editingLine.text.trim()) {
        this.$message.warning('识别文字不能为空')
        return
      }
      const selected = this.materialList.find((m) => m.id === this.editingLine.materialId)
      this.editingLine.materialName = selected ? selected.name : ''
      if (this.editingLine.lineIndex < 0) {
        // 新增标注行：分配本地临时负索引避免与 OCR 行冲突，提交时统一转为 lineIndex=-1
        const tempIdx = this.nextTempIndex--
        const added = { ...this.editingLine, lineIndex: tempIdx, status: '1' }
        this.localAddedLines.push(added)
        this.$set(this.edits, tempIdx, { ...added })
      } else {
        this.$set(this.edits, this.editingLine.lineIndex, { ...this.editingLine })
      }
      this.lineModalVisible = false
    },
    // 保存本图校正结果（整图全部行一次性提交，置 ocr_status='2'）
    handleSaveCorrections() {
      // 带位置信息的行（含拖动/缩放后的最新位置）按 lineIndex 建索引，提交时携带像素坐标
      const lineByIdx = {}
      this.lines.forEach((l) => {
        if (l.left != null && l.top != null && l.width != null && l.height != null) {
          lineByIdx[l.lineIndex] = l
        }
      })
      const lines = Object.keys(this.edits).map((idx) => {
        const i = Number(idx)
        const edit = this.edits[idx]
        const row = {
          lineIndex: i >= 0 ? i : -1,
          text: edit.text,
          materialId: edit.materialId,
          materialName: edit.materialName,
          quantity: edit.quantity,
          unit: edit.unit,
          remark: edit.remark,
          saveAlias: edit.saveAlias
        }
        const withPos = lineByIdx[i]
        if (withPos) {
          row.left = Math.round(withPos.left)
          row.top = Math.round(withPos.top)
          row.width = Math.round(withPos.width)
          row.height = Math.round(withPos.height)
        }
        return row
      })
      if (lines.length === 0 && this.deletedLineIndexes.length === 0) {
        this.$message.warning('该图片无OCR行可校正')
        return
      }
      // 行勾选记为别名，整单无需再传 saveAlias；deletedLineIndexes 为删除行的旧索引集合
      this.saving = true
      saveImgCorrections(this.orderId, this.currentImageId, {
        lines: lines,
        deletedLineIndexes: this.deletedLineIndexes.slice()
      }).then((res) => {
        if (res.code === 200) {
          this.$message.success('本图校正已保存')
          this.resetLineEditState()
          this.imageDetail = res.data || null
          this.edits = this.buildEdits(this.imageDetail)
          this.loadOrder()
        } else {
          this.$message.warning(res.message || '保存失败')
        }
      }).finally(() => {
        this.saving = false
      })
    },
    // XSDD 审核配置（bigType=XSDD）：决定生成按钮三态
    loadXsddAuditConfig() {
      getImgAuditConfig('XSDD').then((res) => {
        if (res.code === 200 && res.data) {
          this.xsddMultiOn = !!res.data.multiOn
          this.xsddFlowOn = !!res.data.flowOn
        }
      })
    },
    // 全部图片校正完成后生成 XSDD 销售订单（audit=true 时生成的订单直接置已审核）
    handleGenerate(audit) {
      this.$confirm({
        title: '确认操作',
        content: audit ? '将按各图校正明细汇总生成销售订单并直接审核，是否继续？'
          : '将按各图校正明细汇总生成销售订单，是否继续？',
        onOk: () => {
          this.generating = true
          generateImgOrder(this.orderId, audit ? { audit: true } : undefined).then((res) => {
            if (res.code === 200) {
              const successMsg = '生成成功，销售订单 ' + ((res.data && res.data.xsddBillNo) || '')
              if (res.data && res.data.creditTip) {
                this.showCreditTip(res.data.creditTip, successMsg)
              } else {
                this.$message.success(successMsg)
                this.goBackList()
              }
            } else {
              this.$message.warning(res.message || '生成失败')
            }
          }).finally(() => {
            this.generating = false
          })
        }
      })
    },
    // 生成 XSDD 后提交审批流程（bigType=1 销售订单）
    handleGenerateFlow() {
      this.$confirm({
        title: '确认操作',
        content: '将按各图校正明细汇总生成销售订单并提交审批流程，是否继续？',
        onOk: () => {
          this.generating = true
          generateImgOrder(this.orderId).then((res) => {
            if (res.code === 200) {
              const xsddBillNo = (res.data && res.data.xsddBillNo) || ''
              if (!xsddBillNo) {
                this.$message.warning('生成成功但未返回单号，无法提交审批流程')
                this.goBackList()
                return
              }
              submitWorkflowTask(1, xsddBillNo).then((r) => {
                const msg = (r && r.code === 200)
                  ? '生成成功并已提交流程，销售订单 ' + xsddBillNo
                  : (((r && r.data && r.data.message) || '提交流程失败') + '，销售订单 ' + xsddBillNo)
                if (res.data && res.data.creditTip) {
                  this.showCreditTip(res.data.creditTip, msg)
                } else {
                  if (r && r.code === 200) {
                    this.$message.success(msg)
                  } else {
                    this.$message.warning(msg)
                  }
                  this.goBackList()
                }
              })
            } else {
              this.$message.warning(res.message || '生成失败')
            }
          }).finally(() => {
            this.generating = false
          })
        }
      })
    },
    goBack() {
      this.goBackList()
    },
    // 信用提示弹窗：成功信息 + 客户信用提示，确认后回列表
    showCreditTip(tip, successMsg) {
      this.$info({
        title: '客户信用提示',
        content: this.$createElement('div', null, [
          this.$createElement('p', { style: 'color: #52c41a; margin-bottom: 8px;' }, successMsg),
          this.$createElement('p', { style: 'color: #fa8c16; font-weight: 600; white-space: pre-wrap; margin: 0;' }, tip)
        ]),
        okText: '返回列表',
        onOk: () => {
          this.goBackList()
        }
      })
    },
    // 按来源列表返回：发图开单进入回 imgoutbound，其余（含直接访问兜底）回 imgorder
    goBackList() {
      const path = this.$route.query.from === 'outbound'
        ? '/plugin/order-receive/imgoutbound' : '/plugin/order-receive/imgorder'
      this.$router.push(path)
    }
  }
}
</script>
<style scoped>
.workbench-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.workbench-title { font-size: 15px; font-weight: 600; display: flex; align-items: center; }
.img-list { display: flex; flex-direction: column; gap: 10px; }
.img-item { border: 1px solid #e8e8e8; border-radius: 4px; padding: 6px; cursor: pointer; }
.img-item.active { border-color: #1890ff; box-shadow: 0 0 0 2px rgba(24,144,255,0.2); }
.img-item:hover { border-color: #1890ff; }
.img-thumb { width: 100%; height: 80px; object-fit: cover; border-radius: 3px; background: #f5f5f5; }
.img-item-footer { display: flex; justify-content: space-between; align-items: center; margin-top: 5px; }
.img-item-name { font-size: 12px; color: #666; }
.img-item-tag { margin-right: 0; }
.img-toolbar { margin-bottom: 12px; display: flex; align-items: center; }
.img-toolbar-hint { margin-left: 12px; font-size: 12px; color: #888; }
.img-stage-wrap { border: 1px solid #f0f0f0; padding: 10px; border-radius: 4px; }
.img-stage { position: relative; display: inline-block; max-width: 100%; }
.img-main { max-width: 100%; display: block; }
.ocr-box { position: absolute; border: 2px solid; cursor: pointer; opacity: 0.85; }
.ocr-box:hover { opacity: 1; background: rgba(24,144,255,0.1); }
.ocr-box-red { border-color: #f5222d; }
.ocr-box-green { border-color: #52c41a; }
.ocr-box-index { position: absolute; top: -2px; left: -2px; background: rgba(0,0,0,0.55); color: #fff; font-size: 11px; line-height: 16px; padding: 0 4px; border-radius: 0 0 3px 0; }
.ocr-box-alias { position: absolute; top: -2px; right: -2px; background: #1890ff; color: #fff; font-size: 11px; line-height: 16px; padding: 0 4px; border-radius: 0 0 0 3px; }
.plain-lines { margin-top: 10px; }
.plain-lines-title { font-size: 12px; color: #888; margin-bottom: 6px; }
.plain-line { padding: 4px 6px; border-radius: 3px; display: flex; align-items: center; }
.plain-line:hover { background: #f5f5f5; }
.plain-line-text { flex: 1; cursor: pointer; min-width: 0; }
.plain-line-del { color: #999; cursor: pointer; margin-left: 8px; padding: 2px; }
.plain-line-del:hover { color: #f5222d; }
.plain-lines-hint { margin-top: 8px; font-size: 12px; color: #999; }
/* ===== 行编辑增强：增加标注画框模式 / 选中高亮 / 缩放手柄 / 拖动新框 ===== */
.img-stage.draw-mode { cursor: crosshair; }
.img-stage.draw-mode .ocr-box { pointer-events: none; }
.ocr-box-selected { border-color: #1890ff !important; box-shadow: 0 0 0 2px rgba(24,144,255,0.35); background: rgba(24,144,255,0.12); }
.ocr-box-resize { position: absolute; right: -5px; bottom: -5px; width: 10px; height: 10px; background: #1890ff; border: 1px solid #fff; cursor: nwse-resize; }
.drawing-box { position: absolute; border: 2px dashed #1890ff; background: rgba(24,144,255,0.08); pointer-events: none; }
</style>
